import { safeDefine } from "@breuni-ui/safe-define";
import { CarouselContainer } from "@breuni-ui/carousel";
import {
  CarouselTrackingData,
  isGiftcardTrackingData,
  isNonEmptyArray,
  isProductTrackingData,
  RecoItemTrackingData,
} from "./tracking/CarouselTrackingData";
import * as EntdCarousel from "./EntdCarousel";
import CubeRecoTracking from "./tracking/cube/CubeRecoTracking";
import GTMRecoTracking from "./tracking/GTM/GTMRecoTracking";
import RichRelevanceTracking from "./tracking/richRelevance/RichRelevanceTracking";
import { debounceFn } from "./debounceFn";
import { entdCarouselContainerTagName } from "./entdCarouselContainerTagName";
import { entdCarouselTagName } from "./entdCarouselTagName";

const isElement = (node: Node): node is HTMLElement =>
  node instanceof HTMLElement;
const getKeyFromPositions: (recoItems: RecoItemTrackingData[]) => string = (
  recoItems,
) => recoItems.map((p) => p.position).join("|");

const slideIndexKey = "data-slide-index" as const;

export default class EntdCarouselContainer extends CarouselContainer {
  private trackingData: CarouselTrackingData | null | undefined;
  private readonly cubeTracking: CubeRecoTracking;
  private readonly gtmTracking: GTMRecoTracking;
  private readonly dataVisibleAttributeObserver: MutationObserver;
  private readonly intersectionObserver: IntersectionObserver;
  private readonly startAndStopObservingSelf: () => void;
  private readonly alreadySeenRecoCombinations: Set<string>;

  constructor() {
    super();
    this.alreadySeenRecoCombinations = new Set();
    this.cubeTracking = new CubeRecoTracking();
    this.gtmTracking = new GTMRecoTracking();
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            this.trackingData = this.extractTrackingDataFromDOM();
            if (this.trackingData) {
              const key = getKeyFromPositions(this.trackingData.recoItems);
              if (!this.alreadySeenRecoCombinations.has(key)) {
                this.alreadySeenRecoCombinations.add(key);
                this.gtmTracking.trackRecoImpression(this.trackingData);
                this.cubeTracking.trackRecoImpression(this.trackingData);
              }
            }
          }
        });
      },
      { threshold: [0.9] },
    );
    this.startAndStopObservingSelf = debounceFn(() => {
      this.intersectionObserver.unobserve(this);
      this.intersectionObserver.observe(this);
    }, 500);
    this.dataVisibleAttributeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation: MutationRecord) => {
        if (
          isElement(mutation.target) &&
          mutation.target.hasAttribute("data-is-slide")
        ) {
          this.startAndStopObservingSelf();
        }
      });
    });
  }

  connectedCallback(): void {
    super.connectedCallback();

    Array.from(this.querySelectorAll<HTMLAnchorElement>("article > a")).forEach(
      (a) => {
        a.addEventListener("click", (event) => {
          this.handleRecommendationClick(event);
        });
      },
    );

    const carousel =
      this.querySelector<EntdCarousel.default>(entdCarouselTagName);
    if (carousel) {
      this.dataVisibleAttributeObserver.observe(carousel, {
        attributes: true,
        subtree: true,
        attributeFilter: ["data-visible"],
      });
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.dataVisibleAttributeObserver.disconnect();
    this.intersectionObserver.disconnect();
  }

  extractTrackingDataFromDOM(): CarouselTrackingData | null {
    const placement = this.getAttribute("placement");
    const strategy = this.getAttribute("strategy");
    const currency = this.getAttribute("currency");
    const headline = this.getAttribute("headline");
    const pageName = this.getAttribute("page-name");
    const produkte = [
      ...this.querySelectorAll<HTMLElement>(
        'article[data-visible="true"][data-item-type="Product"]',
      ),
    ]
      .map((produktEl) => ({
        itemType: produktEl.getAttribute("data-item-type"),
        position: produktEl.getAttribute(slideIndexKey),
        syan: produktEl.getAttribute("syan"),
        farbId: produktEl.getAttribute("farb-id"),
        produktLink: produktEl.getAttribute("produkt-link"),
      }))
      .filter(isProductTrackingData);
    const giftcards = [
      ...this.querySelectorAll<HTMLElement>(
        'article[data-visible="true"][data-item-type="Giftcard"]',
      ),
    ]
      .map((giftcardEl) => ({
        itemType: giftcardEl.getAttribute("data-item-type"),
        position: giftcardEl.getAttribute(slideIndexKey),
        produktLink: giftcardEl.getAttribute("produkt-link"),
      }))
      .filter(isGiftcardTrackingData);
    const recoItems: RecoItemTrackingData[] = [...produkte, ...giftcards];
    if (
      placement !== null &&
      strategy !== null &&
      currency !== null &&
      headline !== null &&
      pageName !== null &&
      isNonEmptyArray(recoItems)
    ) {
      return {
        placement,
        strategy,
        currency,
        headline,
        pageName,
        recoItems,
      };
    } else {
      return null;
    }
  }

  private handleRecommendationClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const target = event.currentTarget as HTMLElement;
    const trackingParam = target.getAttribute("data-tracking-param");
    const position = target.parentElement?.getAttribute(slideIndexKey);
    const href = target.getAttribute("href");
    const farbId = target.parentElement?.getAttribute("farb-id");

    if (trackingParam) {
      RichRelevanceTracking.richRelevanceClickTracking(trackingParam);
    }
    if (this.trackingData) {
      this.gtmTracking.trackRecoClick(this.trackingData);
      if (position) {
        this.cubeTracking.trackRecoClick(this.trackingData, position);
      }
    }

    const hrefWithVariant = href
      ? farbId
        ? `${href}?variant=${farbId}`
        : href
      : "";
    window.open(hrefWithVariant, "_self");
  }
}
safeDefine(entdCarouselContainerTagName, EntdCarouselContainer);
